import {
  BoundMultiSelectField,
  BoundTextAreaField,
  BoundToggleChipGroupField,
  Button,
  Checkbox,
  Chips,
  Css,
  FormLines,
  IconButton,
  Palette,
  useComputed,
  useRightPane,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { useMemo, useState } from "react";
import { InputMaybe, SaveReadyPlanDesignNoteInput } from "src/generated/graphqlTypes";
import { useToggle } from "src/hooks";
import { useNavigateToRpav } from "src/hooks/useNavigateToRpav";
import { queryResult } from "src/utils";
import { useDesignPackageConfiguratorContext } from "../DesignPackageConfiguratorContext";
import {
  DesignPackageDesignNotePane_MetadataQuery,
  DesignPackageDesignNotePane_ReadyPlanDesignNoteFragment,
  useDesignPackageDesignNotePane_MetadataQuery,
  useDesignPackageDesignNotePaneQuery,
  useSaveReadyPlanDesignNoteMutation,
} from "./DesignPackageDesignNotePane.generated";
import { DesignPackageTliCardFragment } from "./DesignPackageItemSlotEditor.generated";

type DesignPackageDesignNotePaneProps = {
  placeholder: DesignPackageTliCardFragment;
};

export function DesignPackageDesignNotePane({ placeholder }: DesignPackageDesignNotePaneProps) {
  const { designPackage } = useDesignPackageConfiguratorContext();
  const query = useDesignPackageDesignNotePaneQuery({
    variables: {
      designPackageVersionId: designPackage.version.id,
      placeholderId: placeholder.id,
    },
  });
  return queryResult(query, ({ takeoffLineItem }) => (
    <DesignPackageDesignNotePaneView
      designNotes={takeoffLineItem.designNotes}
      placeholder={placeholder}
      refetch={query.refetch}
    />
  ));
}

type DesignPackageDesignNotePaneViewProps = {
  designNotes: DesignPackageDesignNotePane_ReadyPlanDesignNoteFragment[];
  placeholder: DesignPackageTliCardFragment;
  refetch: () => void;
};

function DesignPackageDesignNotePaneView({ designNotes, placeholder, refetch }: DesignPackageDesignNotePaneViewProps) {
  const { closeRightPane } = useRightPane();
  const { designPackage } = useDesignPackageConfiguratorContext();
  const [showEditor, toggleShowEditor] = useToggle(false);
  const [editingNoteId, setEditingNoteId] = useState<string | "add">();
  const navigateToRpav = useNavigateToRpav();
  const readOnly = designPackage.canEdit.allowed === false;

  const formState = useFormState({
    config: formConfig,
    readOnly,
    init: {
      input: [designNotes, editingNoteId] as const,
      map: ([rpdns, editingNoteId]) => mapToForm(rpdns, editingNoteId),
      ifUndefined: {
        id: undefined,
        content: undefined,
        targetGroupIds: undefined,
        targetCombinations: undefined,
      },
    },
  });

  // Compute the targetGroupIds so the metadata query refreshes with the latest selections
  const targetGroupIds = useComputed(() => formState.targetGroupIds.value, [formState.targetGroupIds.value]);
  const metadataQuery = useDesignPackageDesignNotePane_MetadataQuery({
    variables: {
      designPackageId: designPackage.id,
      designPackageVersionId: designPackage.version.id,
      targetGroupIds,
    },
  });

  const [save] = useSaveReadyPlanDesignNoteMutation();

  const handleAddNote = () => {
    toggleShowEditor();
    setEditingNoteId("add");
  };

  const openEditNote = (noteId: string) => {
    setEditingNoteId(noteId);
    toggleShowEditor();
  };

  const handleSaveNote = async () => {
    const { data } = await save({ variables: { input: mapToInput(formState, designPackage.id, placeholder.id) } });
    if (formState.value.id === undefined) formState.revertChanges(); // Revert form changes when new note is added

    navigateToRpav(data?.saveReadyPlanDesignNote.readyPlanDesignNote.readyPlan.aggregateDraft?.id);
    await refetch();
    toggleShowEditor();
  };

  const handleDeleteNote = async (noteId: string) => {
    await save({ variables: { input: { id: noteId, active: false } } });
    await refetch();
  };

  return (
    <div css={Css.df.fdc.h100.bgWhite.$}>
      <div css={Css.df.fdc.p3.fg1.oya.$}>
        <div css={Css.df.fdc.gap1.mb3.$}>
          <div css={Css.df.jcsb.aic.$}>
            <div css={Css.xl.$}>{placeholder.customPlaceholderName || placeholder.name}</div>
            <IconButton data-testid="closePane" icon="x" onClick={closeRightPane} />
          </div>
          {showEditor && (
            <div css={Css.w25.ml(-2).$}>
              <Button icon="chevronLeft" variant="quaternary" label="Back" onClick={() => toggleShowEditor()} />
            </div>
          )}
        </div>
        <div css={Css.df.fdc.gap2.$}>
          <div css={Css.df.jcsb.$}>
            <div css={Css.lg.$}>Design Notes</div>
          </div>
          {designNotes.isEmpty && !showEditor ? <div css={Css.gray700.$}>No notes have been added.</div> : null}
          {showEditor ? (
            <DesignNoteEditForm formState={formState} metadataQuery={metadataQuery.data} />
          ) : (
            <DesignPackageDesignNotePaneList
              designNotes={designNotes}
              readOnly={readOnly}
              onEdit={openEditNote}
              onRemove={handleDeleteNote}
            />
          )}
        </div>
      </div>
      <div css={Css.df.fdc.$}>
        <div css={Css.df.jcc.p3.bt.bcGray200.$}>
          <Button
            label={showEditor ? "Save" : "+ Add A Note"}
            variant={showEditor ? "primary" : "quaternary"}
            onClick={showEditor ? handleSaveNote : handleAddNote}
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
}

type DesignPackageDesignNotePaneListProps = {
  designNotes: DesignPackageDesignNotePane_ReadyPlanDesignNoteFragment[];
  onEdit: (noteId: string) => void;
  onRemove: (noteId: string) => Promise<void>;
  readOnly: boolean;
};

function DesignPackageDesignNotePaneList({
  designNotes,
  onEdit,
  readOnly,
  onRemove,
}: DesignPackageDesignNotePaneListProps) {
  return designNotes.map((note) => (
    <div key={note.id} css={Css.df.fdc.gap2.$}>
      <div css={Css.df.jcsb.aic.$}>
        <Chips values={note.targetCombinations.map((combination) => combination.name)} />
        {note.targetCombinations.isEmpty ? <div css={Css.gray700.$}>No combinations selected</div> : null}
        <div css={Css.df.gap1.$}>
          <IconButton icon="pencil" color={Palette.Gray600} onClick={() => onEdit(note.id)} disabled={readOnly} />
          <IconButton icon="trash" color={Palette.Gray600} onClick={() => onRemove(note.id)} disabled={readOnly} />
        </div>
      </div>
      <div css={Css.gray700.$}>{note.content}</div>
      <div css={Css.w100.bb.bcGray300.$} />
    </div>
  ));
}

type FormValue = Omit<SaveReadyPlanDesignNoteInput, "targetCombinations"> & {
  targetCombinations: InputMaybe<string[]>;
};
const formConfig: ObjectConfig<FormValue> = {
  id: { type: "value" },
  content: { type: "value", rules: [required] },
  targetGroupIds: { type: "value", rules: [required] },
  targetCombinations: { type: "value", rules: [required] },
};

type DesignNoteEditFormProps = {
  formState: ObjectState<FormValue>;
  metadataQuery: DesignPackageDesignNotePane_MetadataQuery | undefined;
};
function DesignNoteEditForm({ formState, metadataQuery }: DesignNoteEditFormProps) {
  const options = useMemo(() => {
    const designPackage = metadataQuery?.designPackage;
    return {
      targetGroups: (designPackage?.availableTargetGroups ?? []).map(({ id, name }) => ({ label: name, value: id })),
      combinations: designPackage?.combinations ?? [],
    };
  }, [metadataQuery]);

  const { allCombosSelected, hasSelectedTargetGroups } = useComputed(() => {
    const hasSelectedTargetGroups = formState.targetGroupIds.value?.nonEmpty ?? false;
    const countCombos = formState.targetCombinations.value?.length ?? 0;
    return {
      allCombosSelected: hasSelectedTargetGroups && countCombos === options.combinations.length,
      hasSelectedTargetGroups,
    };
  }, [formState, options.combinations]);

  return (
    <FormLines width="full" gap={3}>
      <BoundTextAreaField field={formState.content} label="Design Notes" />
      <BoundToggleChipGroupField field={formState.targetGroupIds} label="Apply To" options={options.targetGroups} />
      <BoundMultiSelectField
        field={formState.targetCombinations}
        options={options.combinations}
        disabled={!hasSelectedTargetGroups}
        label="Combinations"
      />
      <Checkbox
        label="Select All"
        selected={allCombosSelected}
        disabled={!hasSelectedTargetGroups}
        onChange={(selected) => {
          const combos = selected ? options.combinations.map((c) => c.id) : [];
          formState.targetCombinations.set(combos);
        }}
      />
    </FormLines>
  );
}

function mapToForm(
  rpdns: DesignPackageDesignNotePane_ReadyPlanDesignNoteFragment[],
  editingNoteId: string | undefined,
): FormValue {
  const note = rpdns.compact().find((rpdn) => rpdn.id === editingNoteId);
  return {
    ...note,
    targetCombinations: note?.targetCombinations.map((c) => c.id),
    targetGroupIds: note?.targetGroups.map((g) => g.id),
  };
}

function mapToInput(
  formState: ObjectState<FormValue>,
  readyPlanId: string,
  placeholderId: string,
): SaveReadyPlanDesignNoteInput {
  const { targetCombinations, ...rest } = formState.changedValue;
  return {
    ...rest,
    readyPlanId: readyPlanId,
    takeoffLineItemId: placeholderId,
    targetCombinations: targetCombinations?.map((id) => ({ options: id.split(",") })),
  };
}
